import { HttpService, ServerSentEvent } from "../http/HttpService";
import { HttpError } from "../http/Types";
import { t } from "../i18n";

/*
 * Types are declared here rather than in protocol.ts — the chat endpoint lives in the
 * admin-mcp service, which is not part of the admin-rest protocol generation. The form
 * types mirror ChatController's PageForms / PageForm / FormField / FormOption records,
 * FormUpdate and RejectedFormUpdate, and the stream event payloads mirror ChatTurnStream's
 * TextDelta / ToolStep / StreamError records — keep them in sync. Optional fields are omitted,
 * never sent as null: the server serialises NON_NULL and sizes the snapshot against the
 * same shape.
 */
// A runtime array, not just a type, so entityRoutes.contract.test.ts can check it against admin-mcp's snapshot.
export const PAGE_ENTITY_TYPES = ["user", "account", "transaction", "payment", "admin-user", "user-bonus", "bonus-config", "casino-game"] as const;
export type PageEntityType = (typeof PAGE_ENTITY_TYPES)[number];

/** The entity detail page the admin has open, so "this user" etc. resolves server-side. */
export interface PageContext {
  entityType: PageEntityType;
  entityId: string;
}

export type FieldKind =
  | "text"
  | "textarea"
  | "integer"
  | "float"
  | "number"
  | "checkbox"
  | "select"
  | "multiselect"
  | "searchable-select"
  | "date"
  | "datetime"
  | "json"
  | "email"
  | "tags";

/** Why a field is listed without its value and cannot be set. */
export type ExclusionReason = "secret" | "personal" | "config" | "auto-sensitive" | "unsupported";

/** A single JSON scalar as it travels over the wire. */
export type FieldScalar = string | number | boolean | null;

/** One element of a list value — a multi-select's option value or a tag: a scalar, never null. */
export type FieldValueElement = string | number | boolean;

/**
 * A field value as it travels over the wire. Arrays only for `multiselect` (the selected option
 * values) and `tags` (the tag strings); an array replaces the whole selection when set.
 */
export type FieldValue = FieldScalar | FieldValueElement[];

export interface FieldOption {
  value: FieldScalar;
  label: string;
}

/** One rendered form field as the page's form bridge describes it. */
export interface FieldDescriptor {
  path: string;
  kind: FieldKind;
  label: string;
  hint?: string;
  format?: "epoch-ms";
  value?: FieldValue;
  options?: FieldOption[];
  optionsTruncated?: boolean;
  required?: boolean;
  readOnly?: boolean;
  error?: string;
  excluded?: boolean;
  reason?: ExclusionReason;
  /** Set when the value did not fit the value caps: `value` then holds a prefix, and the field cannot be set. */
  valueTruncated?: boolean;
}

export interface PageForm {
  formId: string;
  title: string;
  fields: FieldDescriptor[];
  /** Set when the form was listed by title only because the snapshot caps were reached. */
  truncated?: boolean;
  /** How many fields beyond the per-form field cap were left out; omitted when none were. */
  omittedFields?: number;
}

/** The forms rendered on the page the admin has open, attached to every chat turn while a form is discovered. */
export interface PageForms {
  route: string;
  entityType?: PageEntityType;
  entityId?: string;
  forms: PageForm[];
}

/** A field value the assistant asked the UI to apply; the browser re-validates before applying. */
export interface FormUpdate {
  formId: string;
  path: string;
  value: FieldValue;
}

/**
 * A field update the server refused before it reached the browser. Every field but the reason
 * may be missing: the label when no listed field matched, form and path when the model sent an
 * empty update.
 */
export interface RejectedFormUpdate {
  formId?: string | null;
  path?: string | null;
  label?: string | null;
  reason: string;
}

export interface ChatRequest {
  conversationId: string;
  message: string;
  pageContext?: PageContext;
  pageForms?: PageForms;
}

export interface ChatReply {
  reply: string;
  /** Entity detail page the assistant asked the UI to open (via its navigation tool). */
  navigateTo?: string;
  /** Field updates the assistant requested (via its form tool); absent when the tool was not called. */
  formUpdates?: FormUpdate[];
  /** Field updates the server refused, with its reason in English; absent when none were. */
  rejectedFormUpdates?: RejectedFormUpdate[] | null;
}

/** `delta` event: a chunk of model text. Text that arrives before a `step` is interim; the reply in `done` is authoritative. */
export interface ChatTextDelta {
  text: string;
}

/** `step` event: a tool round is about to run; counts tool rounds from 1. */
export interface ChatToolStep {
  step: number;
}

/** `error` event: the turn failed after the stream started, to be handled as the same HTTP status would be. */
export interface ChatStreamError {
  status: number;
  error: string;
  message: string;
}

/** The chat stream's events by name, each with its JSON data payload. */
export interface ChatStreamEvents {
  delta: ChatTextDelta;
  step: ChatToolStep;
  done: ChatReply;
  error: ChatStreamError;
}

/** Callbacks for a turn's progress while it streams. */
export interface ChatProgress {
  onDelta?(text: string): void;
  onStep?(step: number): void;
}

export interface ChatSendOptions {
  pageContext?: PageContext;
  pageForms?: PageForms;
  progress?: ChatProgress;
  signal?: AbortSignal;
}

/**
 * One conversational turn against the admin-mcp AI chat endpoint. Conversation history is
 * held server-side per conversationId; the admin SESSION cookie rides along so tool calls
 * run with the caller's identity.
 */
export class ChatService {
  constructor(private http: HttpService) {}

  /**
   * Resolves with the turn's reply once the stream's `done` event arrives, reporting text and tool
   * rounds to `progress` meanwhile. Always settles: a stream that ends or breaks without a `done`
   * rejects, so the caller is never left waiting on a dropped connection.
   */
  sendMessage(conversationId: string, message: string, options: ChatSendOptions = {}): Promise<ChatReply> {
    const { pageContext, pageForms, progress, signal } = options;
    const request: ChatRequest = { conversationId, message, pageContext, pageForms };
    return new Promise((resolve, reject) => {
      let settled = false;
      const onEvent = ({ name, data }: ServerSentEvent): void => {
        if (settled || !isChatStreamEvent(name)) {
          return;
        }
        const event = parseStreamEvent(name, data);
        if (event === undefined) {
          settled = true;
          reject(connectionLost());
        } else if (event.name === "delta") {
          progress?.onDelta?.(event.payload.text);
        } else if (event.name === "step") {
          progress?.onStep?.(event.payload.step);
        } else if (event.name === "done") {
          settled = true;
          resolve(event.payload);
        } else {
          settled = true;
          reject(this.http.rejectionFor(event.payload.status, "application/json", event.payload));
        }
      };
      // A progress callback that throws ends the read, so it lands here and settles the turn too.
      this.http
        .postEventStream("/admin-mcp/chat", request, onEvent, signal)
        .then(() => reject(connectionLost()))
        .catch((error: unknown) => reject(isHttpError(error) ? error : connectionLost()));
    });
  }
}

type ChatStreamEventName = keyof ChatStreamEvents;

type ParsedChatStreamEvent = { [Name in ChatStreamEventName]: { name: Name; payload: ChatStreamEvents[Name] } }[ChatStreamEventName];

function isChatStreamEvent(name: string): name is ChatStreamEventName {
  return name === "delta" || name === "step" || name === "done" || name === "error";
}

// A payload that is not a JSON object means the stream was mangled on the way, so the turn's outcome is unknown.
function parseStreamEvent(name: ChatStreamEventName, data: string): ParsedChatStreamEvent | undefined {
  try {
    const payload: unknown = JSON.parse(data);
    return typeof payload === "object" && payload !== null ? ({ name, payload } as ParsedChatStreamEvent) : undefined;
  } catch {
    return undefined;
  }
}

// Built per rejection: i18n has not loaded its translations when this module is first evaluated.
function connectionLost(): HttpError {
  return { message: t("ai-chat.connection-lost", undefined, "The connection to the assistant was lost"), status: 0 };
}

function isHttpError(error: unknown): error is HttpError {
  return typeof error === "object" && error !== null && typeof (error as HttpError).status === "number";
}
